import React from 'react';
import { createRoot } from 'react-dom/client';
import { t } from '../i18n';

// Подтверждение — окном по центру, а не уголком экрана.
//
// Раньше вопрос приходил тем же способом, каким сообщают «сохранено»: мелкой
// плашкой справа сверху. Но «сохранено» читать не обязательно, а на вопрос надо
// ответить — и ответ этот часто необратимый: отгрузить, списать, начать смену
// не на той плите. У пресса вдобавок стоят в метре от монитора и в перчатках:
// угол экрана там просто не смотрят.
//
// Поэтому окно по центру, с затемнением вокруг: пока не ответишь, ничего
// другого сделать нельзя. Ровно так это и должно работать.
//
// Ishlatish:  if (!await confirmToast('Matn?')) return;
//             await confirmToast('…', { big: true })   — крупнее, для цеха
//
// Матнлар РУСЧА — интерфейс тили русча, ўзбекчаси луғатдан t() орқали келади.
export function confirmToast(message, opts = {}) {
  const {
    confirmText = t('Да'),
    cancelText = t('Нет'),
    danger = false,
    title = t('Подтвердите'),
    big = false,
    // Сколько секунд кнопка «да» недоступна. Ноль — как раньше, сразу.
    countdown = 0,
  } = opts;

  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);

    let done = false;
    let tick = null;
    const close = (value) => {
      if (done) return;          // двойное нажатие ничего не ломает
      done = true;
      if (tick) clearInterval(tick);
      document.removeEventListener('keydown', onKey);
      resolve(value);
      // Размонтируем после ответа, а не во время него: React не любит, когда
      // дерево сносят прямо из обработчика собственного события.
      setTimeout(() => { root.unmount(); host.remove(); }, 0);
    };

    // Пока идёт отсчёт, «да» не отвечает — ни кнопкой, ни клавишей. Смысл
    // отсчёта в том, чтобы человек прочитал написанное, а не в том, чтобы
    // задержать мышь: Enter, пройденный не глядя, обходит его в один щелчок.
    let left = countdown;

    // С клавиатуры отвечают в кабинете: Enter — да, Esc — нет.
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      if (e.key === 'Enter' && left <= 0) { e.preventDefault(); close(true); }
    };
    document.addEventListener('keydown', onKey);

    const accent = danger ? '#ef4444' : 'var(--accent, #14b8a6)';

    const paint = () => root.render(
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 4000,
          background: '#000000c4',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
          animation: 'ctBack .14s ease',
        }}
        // Клик мимо окна не отвечает за человека: вопрос задан, и промах по
        // тёмному полю — это не «нет». В цеху мимо попадают постоянно.
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: 'var(--card, #1b1f24)',
            border: `${big ? 3 : 1}px solid ${big ? accent : 'var(--border, #333)'}`,
            borderRadius: big ? 20 : 14,
            boxShadow: '0 24px 70px rgba(0,0,0,.55)',
            padding: big ? '30px 34px' : '22px 24px',
            width: '100%',
            maxWidth: big ? 620 : 460,
            textAlign: big ? 'center' : 'left',
            animation: 'ctIn .16s ease',
          }}
        >
          {big ? (
            <div style={{ fontSize: 54, lineHeight: 1, marginBottom: 12 }}>
              {danger ? '⚠️' : '❓'}
            </div>
          ) : null}

          <div style={{
            display: 'flex', gap: 12,
            alignItems: 'flex-start',
            flexDirection: big ? 'column' : 'row',
          }}>
            {!big && (
              <div style={{
                width: 38, height: 38, flexShrink: 0, borderRadius: 10,
                display: 'grid', placeItems: 'center', fontSize: 19,
                background: danger ? '#ef444420' : 'var(--accent-weak, #14b8a620)',
                color: accent,
              }}>
                {danger ? '⚠️' : '❓'}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
              <div style={{
                fontWeight: big ? 900 : 700,
                color: big ? accent : 'var(--text, #eee)',
                fontSize: big ? 30 : 17,
                marginBottom: big ? 14 : 5,
                textTransform: big ? 'uppercase' : 'none',
                letterSpacing: big ? '.01em' : 0,
              }}>
                {title}
              </div>
              {/* Перенос строк из сообщения сохраняем: в них разбивают
                  перечисления вроде «чего именно не хватает». */}
              <div style={{
                color: big ? 'var(--text, #eee)' : 'var(--muted, #bbb)',
                fontSize: big ? 21 : 15,
                lineHeight: 1.45,
                whiteSpace: 'pre-line',
              }}>{message}</div>
            </div>
          </div>

          <div style={{
            display: 'flex', gap: big ? 14 : 10,
            marginTop: big ? 26 : 20,
            justifyContent: big ? 'stretch' : 'flex-end',
          }}>
            <button
              onClick={() => close(false)}
              className="touch-btn"
              style={{
                flex: big ? 1 : '0 0 auto',
                padding: big ? '20px 10px' : '11px 18px',
                borderRadius: big ? 14 : 10,
                cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--bg, transparent)',
                border: '2px solid var(--border, #444)',
                color: 'var(--muted, #bbb)',
                fontSize: big ? 19 : 14, fontWeight: 700,
              }}
            >
              {cancelText}
            </button>
            <button
              onClick={() => { if (left <= 0) close(true); }}
              className="touch-btn"
              autoFocus
              disabled={left > 0}
              style={{
                flex: big ? 2 : '0 0 auto',
                padding: big ? '20px 10px' : '11px 22px',
                borderRadius: big ? 14 : 10,
                cursor: left > 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', border: 'none',
                background: left > 0 ? 'var(--border, #444)' : accent,
                color: left > 0 ? 'var(--dim, #888)' : '#fff',
                fontSize: big ? 19 : 14, fontWeight: 800,
              }}
            >
              {left > 0 ? `${confirmText} · ${left}` : confirmText}
            </button>
          </div>
        </div>
      </div>
    );

    paint();
    if (left > 0) {
      tick = setInterval(() => {
        left -= 1;
        paint();
        if (left <= 0) clearInterval(tick);
      }, 1000);
    }
  });
}

// Необратимое подтверждают трижды, и каждый раз ждут отсчёт.
//
// Одного окна мало не потому, что человек не понял, — а потому, что он в этот
// момент уже решил и читает не текст, а расположение кнопок. Первое окно он
// закрывает по памяти, второе тоже. Отсчёт ломает именно это: кнопки нет,
// закрыть по памяти нечего, остаётся прочитать.
//
// Три разных текста, а не один трижды: каждый шаг называет свою цену. Что
// исчезнет — сколько это стоит — можно ли вернуть.
//
//   if (!await dangerConfirm({ title: 'Очистка', steps: ['…', '…', '…'] })) return;
export async function dangerConfirm({ title, steps, confirmText = t('Продолжить'),
                                      seconds = 10, finalText }) {
  const list = (steps || []).filter(Boolean);
  for (let i = 0; i < list.length; i++) {
    const last = i === list.length - 1;
    const ok = await confirmToast(list[i], {
      title: `${title} · ${i + 1} из ${list.length}`,
      danger: true,
      countdown: seconds,
      confirmText: last ? (finalText || t('Да, выполнить')) : confirmText,
      cancelText: t('Отмена'),
    });
    if (!ok) return false;
  }
  return true;
}

// Animatsiya keyframes'larini bir marta qo'shamiz
if (typeof document !== 'undefined' && !document.getElementById('ct-kf')) {
  const st = document.createElement('style');
  st.id = 'ct-kf';
  st.textContent =
    '@keyframes ctIn{from{opacity:0;transform:translateY(-10px) scale(.97)}to{opacity:1;transform:none}}' +
    '@keyframes ctBack{from{opacity:0}to{opacity:1}}';
  document.head.appendChild(st);
}

export default confirmToast;
